<template>
    <div v-if="!baseStore.isLoading" style="padding-bottom: 100px">
        <v-container>
            <v-row>
                <v-col>
                    <v-tabs v-if="baseStore.isToday && showMpcAvvik" v-model="viewSelection" height="30" class="selection-tabs">
                        <v-tab key="mpc" data-id="mpc" @click="setView(Views[ViewIndex.mpc])"> MPC </v-tab>
                        <v-tab key="avvik" data-id="avvik" @click="setView(Views[ViewIndex.avvik])"> Avvik </v-tab>
                    </v-tabs>
                </v-col>
                <v-col>
                    <div class="date body-2 text-right mr-2">
                        {{ dayString(baseStore.selectedDate) }} {{ date(baseStore.selectedDate) }}
                    </div>
                </v-col>
            </v-row>
        </v-container>
        <v-container v-if="selectedView === Views[ViewIndex.mpc]">
            <v-row no-gutters>
                <v-col class="flex-grow-0 flex-shrink-1">
                    <v-btn
                        variant="text"
                        elevation="0"
                        class="underline text-none pa-0"
                        data-id="prev-day"
                        :disabled="!baseStore.isOnline"
                        @click="prevDay"
                    >
                        Dagen før
                    </v-btn>
                </v-col>
                <v-spacer />
                <v-col class="flex-grow-0 flex-shrink-1">
                    <v-btn
                        variant="text"
                        elevation="0"
                        class="underline text-none pa-0"
                        data-id="next-day"
                        :disabled="!baseStore.isOnline"
                        @click="nextDay"
                    >
                        Dagen etter
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>

        <v-snackbar v-model="baseStore.isFutureDay" :timeout="timeout" top> Du blar gjennom fremtidige datoer </v-snackbar>

        <v-snackbar v-model="baseStore.isPastDay" :timeout="timeout" top> Du blar gjennom tidligere datoer </v-snackbar>

        <line-departure-list
            v-if="departures && departures.length > 0"
            :value="departures"
            :line-number="lineNumber"
            :is-today="baseStore.isToday"
            :selected-view="selectedView"
        />
        <div v-else-if="departures && departures.length === 0">
            <p class="text-center">
                <v-icon size="36"> mdi-alert-outline </v-icon>
            </p>
            <h3 class="headline text-center">Ingen avganger</h3>
            <p class="body-2 text-center">
                Ingen avganger for denne ruten ruten ble funnet i det aktuelle tidsrommet.
                <br />Vennligst forsøk igjen for et annet tidsrom, eller gå tilbake og velg en annen rute.
            </p>
        </div>
        <v-overlay v-else>
            <v-progress-circular indeterminate size="64" />
        </v-overlay>

        <v-footer color="transparent" padless fixed height="120" class="gradient-bg pt-6">
            <v-row no-gutters justify="center">
                <v-col cols="6">
                    <v-btn block color="primary" large class="text-none" @click="navigate({ name: 'home' })"> Tilbake </v-btn>
                </v-col>
            </v-row>
        </v-footer>
    </div>
</template>

<script lang="ts" setup>
import LineDepartureList from '@/features/departures/DepartureList.vue';
import { useEmitter, Events } from '@/plugins/emitter';
import { ISODateString, dayString } from '@/helpers/DateHelpers';
import useNavigate from '@/plugins/navigate';
import { ref, computed, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Views, ViewIndex, getViewName, getViewIndex } from '@/helpers/ViewSwitcher';
import { useBaseStore } from '@/features/shared/baseStore';
import { useLinesStore } from '@/features/lines/linesStore';
import { useSettingsStore } from '@/features/shared/settingsStore';
import { useTripsStore } from '@/features/trips/tripsStore';
import { useDateTimeFilters } from '@/helpers/useDateTimeFilters';

interface Props {
    lineNumber: string;
}
const props = defineProps<Props>();

const { date } = useDateTimeFilters();
const eventbus = useEmitter();
const timeout = -1;
const syncInterval = ref(undefined);
const syncTimeout = 60000; // 1 min
const viewSelection = ref(null);
const baseStore = useBaseStore();
const settingsStore = useSettingsStore();
const linesStore = useLinesStore();
const tripsStore = useTripsStore();
const { navigate } = useNavigate();
const router = useRouter();
const route = useRoute();

eventbus.emitter.on(Events.dateChanged, fetchTripsSync);
eventbus.emitter.emit(Events.menuUpdate, 'departures');

const showMpcAvvik = computed(() => {
    return settingsStore.settings && settingsStore.showMpcAvvik;
});

const showMpc = computed(() => {
    return settingsStore.settings && settingsStore.showMpc;
});

const selectedView = computed(() => {
    return getViewName(viewSelection.value);
});

const lineName = computed(() => {
    return currentLine.value?.name ?? '';
});

const currentLine = computed(() => {
    return linesStore.getLineByNumber(baseStore.lineNumber);
});

const departures = computed(() => {
    return tripsStore.departures;
});

async function initialize() {
    if (!showMpcAvvik.value) {
        const view = showMpc.value ? Views[ViewIndex.mpc] : Views[ViewIndex.avvik];
        setView(view);
    }
    baseStore.lineNumber = props.lineNumber;
    baseStore.clearTripMenuItem();
    await fetchTrips();
    baseStore.departuresPage = { pageName: `Avganger (${lineName.value})`, urlName: 'departures' };
    if (settingsStore.isReadOnly) {
        syncInterval.value = setInterval(syncTrips, syncTimeout);
    }
    viewSelection.value = getViewIndex(router.currentRoute.value.query.view);
}

function fetchTripsSync() {
    (async () => await fetchTrips())();
}

async function fetchTrips(backgroundSync = false) {
    await tripsStore.fetchTrips({
        lineNumber: props.lineNumber,
        calendarDay: baseStore.calendarDay,
        backgroundSync
    });
}

async function syncTrips() {
    return fetchTrips(true);
}

async function prevDay() {
    const date = new Date(baseStore.selectedDate);
    date.setDate(date.getDate() - 1);
    await changeDate(date);
}

async function nextDay() {
    const date = new Date(baseStore.selectedDate);
    date.setDate(date.getDate() + 1);
    await changeDate(date);
}

async function changeDate(date: Date) {
    const dateString = ISODateString(date);
    const query = { ...route.query };

    // if same, do nothing
    if (dateString === query.date) return;

    const today = new Date();
    const isToday =
        date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();

    if (isToday) {
        if (query.date) {
            // remove date query
            query.date = null;
            await navigate({ query });
        }
    } else if (dateString) {
        // add/update date query
        query.date = dateString;
        await navigate({ query });
    }
}

function setView(view: string) {
    if (router.currentRoute.value.query.view !== view) {
        router.replace({ name: 'departures', query: { token: router.currentRoute.value.query.token, view } });
        viewSelection.value = getViewIndex(view);
    }
}

onBeforeUnmount(() => {
    if (syncInterval.value) {
        clearInterval(syncInterval.value);
        syncInterval.value = undefined;
    }
    eventbus.emitter.off(Events.dateChanged, fetchTripsSync);
});

initialize();
</script>

<style>
.underline .v-btn__content {
    display: inline-block;
    border-bottom: 2px solid black;
    padding-bottom: 4px;
}

.center-notification-text {
    justify-content: center !important;
}
</style>
@/services/emitter@/views/navigate
